import { ref as dbRef, get, onValue, set } from 'firebase/database';
import { db, auth } from '../firebase';
import {
  REDEPLOY_ENDPOINT_PATH,
  DEPLOY_STATUS_PATH,
  DEPLOY_STATUS_BASELINE,
  DEPLOY_STATUS_RUNNING,
  DEPLOY_STATUS_FAILED,
  REDEPLOY_RUN_STALE_MS,
  DEPLOY_BASELINE_ENTITY_ID,
  REDEPLOY_BANNER_DELAY_MS,
} from '../constants/bookingConst';
import {
  logAuditEvent,
  resolveAdminActor,
  AUDIT_ACTION_TYPES,
  ENTITY_TYPES,
} from './auditLogUtil';

/**
 * Reads the last successful deploy of this club's site.
 *
 * Written only by the Redeploy client workflow, with a service account, so the
 * database rules make this node read-only to every client (see
 * database.rules.json). A missing node is not an error: it means this club has
 * never been redeployed through the button, which is the normal state until
 * the first one runs.
 *
 * @returns {Promise<{lastDeployedAt: number|null, lastDeployedBy: string|null,
 *   lastRunUrl: string|null, lastStatus: string|null}>}
 */
export const fetchDeployStatus = async () => {
  const empty = {
    lastDeployedAt: null, lastDeployedBy: null, lastRunUrl: null,
    lastRunId: null, lastStatus: null, startedAt: null,
  };
  if (!db) return empty;

  try {
    const snapshot = await get(dbRef(db, DEPLOY_STATUS_PATH));
    const value = snapshot.val();
    if (!value || typeof value !== 'object') return empty;

    return {
      lastDeployedAt: Number(value.lastDeployedAt) || null,
      lastDeployedBy: value.lastDeployedBy || null,
      lastRunUrl: value.lastRunUrl || null,
      lastRunId: value.lastRunId || null,
      lastStatus: value.lastStatus || null,
      startedAt: Number(value.startedAt) || null,
    };
  } catch (error) {
    // A read failure must not break the screen this is rendered on. The cost
    // of being wrong here is a banner that does not show, which is the same
    // state the app was in before the banner existed.
    console.error('Could not read deploy status:', error);
    return empty;
  }
};

/**
 * Seeds /deployStatus the first time an admin signs in on a club that has
 * no baseline yet.
 *
 * WHY THIS EXISTS. The pending-changes banner compares config.lastModified
 * against deployStatus/lastDeployedAt, and stays hidden while there is no
 * baseline to compare against (getPendingChanges' hasEverDeployed). That
 * baseline normally comes from scripts/recordDeploy.js as a postdeploy hook
 * — so a club released before that hook existed, or one whose hook failed
 * (no --account for a project on a second Firebase account, a CLI timeout),
 * has no node, never shows the banner, and can therefore never reach the
 * Redeploy button at all. One write on admin login closes that gap without
 * anyone opening the Firebase console.
 *
 * The timestamp is NOW, not zero: as far as anything here can tell, the live
 * site does match the current config. The cost is that a change saved
 * BEFORE the seed does not raise the banner — the next save does. Zero would
 * be worse, since it would claim every club is out of date on day one, which
 * is exactly what hasEverDeployed exists to avoid.
 *
 * Writes only when the node is absent, and the database rules allow this
 * create once and never a rewrite — so a later sign-in can neither move the
 * baseline nor overwrite what a real deploy recorded.
 *
 * NEVER THROWS. It runs on the login path, and a database hiccup must not
 * keep an admin out of their own screen. A skipped seed costs a banner that
 * appears one save later.
 *
 * @returns {Promise<{created: boolean, lastDeployedAt: number|null}>}
 */
export const ensureDeployStatusBaseline = async () => {
  if (!db) return { created: false, lastDeployedAt: null };

  try {
    const snapshot = await get(dbRef(db, DEPLOY_STATUS_PATH));
    const existing = Number(snapshot.val()?.lastDeployedAt) || null;
    if (existing) return { created: false, lastDeployedAt: existing };

    const lastDeployedAt = Date.now();
    const actor = resolveAdminActor();

    await set(dbRef(db, DEPLOY_STATUS_PATH), {
      lastDeployedAt,
      lastDeployedBy: actor,
      lastStatus: DEPLOY_STATUS_BASELINE,
    });

    await logAuditEvent({
      actionType: AUDIT_ACTION_TYPES.DEPLOY_BASELINE_SET,
      entityType: ENTITY_TYPES.DEPLOYMENT,
      entityId: DEPLOY_BASELINE_ENTITY_ID,
      performedBy: actor,
      statusNew: DEPLOY_STATUS_BASELINE,
      summary: {
        lastDeployedAt,
        note: 'No deploy baseline existed for this club; seeded on admin login.',
      },
    });

    return { created: true, lastDeployedAt };
  } catch (error) {
    // PERMISSION_DENIED is the expected outcome of a race rather than a
    // misconfiguration: two admins signing in together, the second create
    // refused because the first one landed. Nothing to repair either way.
    console.error('Could not seed the deploy baseline:', error);
    return { created: false, lastDeployedAt: null };
  }
};

/**
 * Watches the deploy status, so the banner clears itself when a run finishes
 * instead of waiting for the admin to reload.
 *
 * @param {(status: object) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribeDeployStatus = (callback) => {
  if (!db) return () => {};

  const unsubscribe = onValue(
    dbRef(db, DEPLOY_STATUS_PATH),
    (snapshot) => {
      const value = snapshot.val() || {};
      callback({
        lastDeployedAt: Number(value.lastDeployedAt) || null,
        lastDeployedBy: value.lastDeployedBy || null,
        lastRunUrl: value.lastRunUrl || null,
        lastRunId: value.lastRunId || null,
        lastStatus: value.lastStatus || null,
        startedAt: Number(value.startedAt) || null,
      });
    },
    (error) => { console.error('Deploy status listener failed:', error); },
  );

  return unsubscribe;
};

/**
 * What the last recorded run is doing right now.
 *
 * Read from the database rather than component state so that EVERY admin,
 * on every device, sees a run in progress — not just the tab that pressed
 * the button. Before this, a reload during a four-minute deploy showed an
 * idle 'Redeploy now' button, which invited a second run of a job that only
 * queues behind the first.
 *
 * A run that claims to be running past REDEPLOY_RUN_STALE_MS is reported as
 * stale rather than running: a cancelled workflow never clears its own
 * marker, and a button disabled forever is worse than one offered early.
 *
 * @param {object|null} deployStatus - from fetchDeployStatus
 * @param {number} [now] - injectable for tests
 * @returns {{running: boolean, stale: boolean, failed: boolean,
 *   startedAt: number|null, runUrl: string|null}}
 */
export const getDeployRunState = (deployStatus, now = Date.now()) => {
  const status = deployStatus?.lastStatus || null;
  const startedAt = Number(deployStatus?.startedAt) || null;
  const claimsRunning = status === DEPLOY_STATUS_RUNNING;
  const stale = Boolean(
    claimsRunning && (startedAt === null || (now - startedAt) > REDEPLOY_RUN_STALE_MS),
  );

  return {
    running: claimsRunning && !stale,
    stale,
    failed: status === DEPLOY_STATUS_FAILED,
    startedAt,
    runUrl: deployStatus?.lastRunUrl || null,
  };
};

/**
 * Whether this club has config changes that visitors cannot see yet.
 *
 * The comparison is `config.lastModified` (stamped by ConfigContext's
 * updateConfig on every save) against `lastDeployedAt` (written by the
 * workflow). A club that has never deployed through the button has no
 * lastDeployedAt, so EVERY club would show the banner on day one — hence the
 * `hasEverDeployed` flag, which lets the caller stay quiet until the first
 * redeploy establishes a baseline.
 *
 * @param {object|null} config - the club config
 * @param {object|null} deployStatus - from fetchDeployStatus
 * @param {number} [now] - injectable for tests
 * @returns {{pending: boolean, hasEverDeployed: boolean,
 *   changedAt: number|null, deployedAt: number|null}}
 */
export const getPendingChanges = (config, deployStatus, now = Date.now()) => {
  const changedAt = Number(config?.lastModified) || null;
  const deployedAt = Number(deployStatus?.lastDeployedAt) || null;

  // Settled only after the grace period: without it the banner appears the
  // instant an admin saves, while they are still editing, and nags through a
  // run of small changes. The moment to redeploy is after the editing stops.
  const settled = changedAt !== null && (now - changedAt) >= REDEPLOY_BANNER_DELAY_MS;

  return {
    pending: Boolean(settled && deployedAt !== null && changedAt > deployedAt),
    hasEverDeployed: deployedAt !== null,
    changedAt,
    deployedAt,
  };
};

/**
 * Asks the backend to redeploy this club's site.
 *
 * Sends ONLY a Firebase ID token. The backend reads both the club and the
 * caller off that token's verified claims and ignores the request body
 * entirely, so there is nothing useful to put in one — an earlier design
 * posted the admin's email, which anyone could have forged.
 *
 * @returns {Promise<{ok: boolean, status: number, message: string,
 *   runUrl?: string|null, retryAfterSec?: number}>}
 */
/**
 * The backend's API ROOT, derived from the one URL clients already configure.
 *
 * REACT_APP_EMAIL_ENDPOINT_BASE_URL does not point at the API root: it points
 * at the email routes' own prefix, e.g.
 *   https://courtbooking-be.vercel.app/api/bookings/confirmation
 * because emailServiceUtil.js appends "/resend" or "/nodemailer" to it. Naively
 * appending "/redeploy" to the same value gives
 *   .../api/bookings/confirmation/redeploy
 * which is a 404 — the endpoint is at /api/redeploy.
 *
 * So the root is taken as everything up to and including the first "/api"
 * segment, which is true of every client's value today and of a plain
 * "http://localhost:5000/api/..." in development. If there is no /api segment
 * at all, the URL's own origin plus /api is the best remaining guess.
 *
 * Deriving it beats adding a second environment variable: 36 client branches
 * each carry their own .env, and a new variable would be absent from every one
 * of them until each is edited and redeployed.
 *
 * @param {string} base - REACT_APP_EMAIL_ENDPOINT_BASE_URL
 * @returns {string} the API root, with no trailing slash
 */
export const resolveApiRoot = (base) => {
  const trimmed = String(base || '').replace(/\/+$/, '');
  if (!trimmed) return '';

  const match = /^(.*?\/api)(?:\/|$)/.exec(trimmed);
  if (match) return match[1];

  try {
    return `${new URL(trimmed).origin}/api`;
  } catch {
    // Not an absolute URL (a relative base, or a typo). Returning it unchanged
    // keeps the failure visible as a 404 rather than a thrown TypeError that
    // takes the config screen down with it.
    return trimmed;
  }
};

export const requestRedeploy = async () => {
  const base = resolveApiRoot(process.env.REACT_APP_EMAIL_ENDPOINT_BASE_URL);
  if (!base) {
    return {
      ok: false,
      status: 0,
      message: 'No backend is configured for this site, so it cannot redeploy itself.',
    };
  }

  const user = auth?.currentUser;
  if (!user) {
    return { ok: false, status: 401, message: 'Sign in again to redeploy.' };
  }

  let token;
  try {
    // Not force-refreshed: the SDK refreshes a token that is close to expiry
    // on its own, and forcing it costs a round trip on every click.
    token = await user.getIdToken();
  } catch (error) {
    return { ok: false, status: 401, message: 'Could not read your sign-in. Sign in again and retry.' };
  }

  try {
    const response = await fetch(`${base}${REDEPLOY_ENDPOINT_PATH}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });

    let body = {};
    try { body = await response.json(); } catch { body = {}; }

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        // The backend's own wording is better than anything generic: it knows
        // whether this is an expired token, a club missing from the allowlist,
        // or a cooldown, and says which.
        message: body.message || `The redeploy could not be started (${response.status}).`,
        retryAfterSec: Number(body.retryAfterSec) || 0,
      };
    }

    return {
      ok: true,
      status: response.status,
      message: body.message || 'Redeploy started.',
      runUrl: body.runUrl || null,
    };
  } catch (error) {
    // Network-level failure: the browser could not reach the backend at all.
    return {
      ok: false,
      status: 0,
      message: 'Could not reach the server. Check your connection and try again.',
    };
  }
};
